import { useState, useCallback, useRef, useEffect } from 'react';
import { Users, Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCampaignStore } from '@/stores';
import { useFinanceContacts } from '@/hooks/queries/useFinanceContacts';
import { ContactType, DonorGiving } from '@signalvote/shared';
import type { FinanceContactListParams } from '@signalvote/shared';
import { ContactCard } from './ContactCard';
import { AddContactDialog } from './AddContactDialog';

interface ContactListProps {
  onSelectContact: (contactId: string) => void;
  onGoToInbox?: () => void;
}

type TypeFilter = 'All' | 'Donors' | 'Prospects' | 'Payees';

export function ContactList({ onSelectContact, onGoToInbox }: ContactListProps) {
  const canDo = useCampaignStore((s) => s.canDo('Finance'));

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('All');
  const [page, setPage] = useState(1);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Every chip but Prospects means "people in our books": a donor who has never given is never
  // counted there, so the headline number and its pagination stay honest after a prospect upload.
  // Giving history is donor-scoped server-side, so payees are never hidden by it.
  const filterParams: Record<TypeFilter, Pick<FinanceContactListParams, 'type' | 'donorGiving'>> = {
    All: { type: undefined, donorGiving: DonorGiving.HasGiven },
    Donors: { type: ContactType.Donor, donorGiving: DonorGiving.HasGiven },
    Prospects: { type: undefined, donorGiving: DonorGiving.NotGiven },
    Payees: { type: ContactType.Payee, donorGiving: DonorGiving.HasGiven },
  };

  const params: FinanceContactListParams = {
    page,
    pageSize: 20,
    ...filterParams[typeFilter],
    search: debouncedSearch || undefined,
  };

  const { data, isLoading, isError } = useFinanceContacts(params);
  // Whether any prospects exist at all — a book holding only an uploaded list is not empty.
  const { data: prospectProbe } = useFinanceContacts({
    page: 1,
    pageSize: 1,
    donorGiving: DonorGiving.NotGiven,
  });

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  }, []);

  const handleTypeFilter = useCallback((filter: TypeFilter) => {
    setTypeFilter(filter);
    setPage(1);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-muted-foreground">Loading contacts...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-destructive">Failed to load contacts. Please try again.</p>
      </div>
    );
  }

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / (data?.pageSize ?? 20)));

  const hasProspects = (prospectProbe?.totalCount ?? 0) > 0;

  if (totalCount === 0 && !debouncedSearch && typeFilter === 'All' && !hasProspects) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
          <Users className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-muted-foreground">
            No contacts yet. Add your first donor or payee, or import transactions to get started.
          </p>
          {onGoToInbox && (
            <button className="text-sm text-primary underline" onClick={onGoToInbox}>
              Go to inbox
            </button>
          )}
        </div>
        {canDo && (
          <>
            <div className="flex justify-end px-4">
              <button
                type="button"
                onClick={() => setShowAddDialog(true)}
                className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" /> Add Contact
              </button>
            </div>
            <AddContactDialog open={showAddDialog} onOpenChange={setShowAddDialog} />
          </>
        )}
      </div>
    );
  }

  const typeOptions: TypeFilter[] = ['All', 'Donors', 'Prospects', 'Payees'];

  return (
    <div className="space-y-4">
      {/* Header with search and add button */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search contacts..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full rounded-md border pl-9 pr-3 py-2 text-sm"
          />
        </div>
        {canDo && (
          <button
            type="button"
            onClick={() => setShowAddDialog(true)}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Add Contact
          </button>
        )}
      </div>

      {/* Honest note: automatic duplicate detection matches new entries against
          the standardized address on file. Older contacts imported before this
          feature have no standardized address yet, so a new gift from one of
          them may create a second record until the one-time address cleanup has
          run. Kept minimal and non-alarming per the treasurer-first tone. */}
      <p className="text-xs text-muted-foreground">
        Automatic duplicate detection improves once your existing contacts have
        standardized addresses. Until that one-time cleanup runs, a new gift may
        occasionally create a second record for an older contact — you can merge
        them from the contact.
      </p>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          {typeOptions.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => handleTypeFilter(type)}
              className={cn(
                'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                typeFilter === type
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent',
              )}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* Count */}
      <div className="text-sm text-muted-foreground">
        {totalCount} {typeFilter === 'Prospects' ? 'prospects' : 'contacts'}
      </div>

      {/* Contact cards */}
      <div className="space-y-2">
        {items.map((contact) => (
          <ContactCard
            key={contact.id}
            contact={contact}
            onClick={onSelectContact}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-md p-1 hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded-md p-1 hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <AddContactDialog open={showAddDialog} onOpenChange={setShowAddDialog} />
    </div>
  );
}
